// Registering a new vehicle (demo case 2): the plate, the register fills the technical data, the
// administrator adds what only the organisation knows, and the card is complete.
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { RegisterLookup } from '@aldorock-fordon/shared/api-types';
import { api, ApiError } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { ErrorSummary, TextArea, TextField } from '../ui/Fields.tsx';
import { Facts } from '../ui/Facts.tsx';
import {
  ECONOMY_FIELDS,
  ORG_FIELDS,
  PLACE_FIELDS,
  REGISTER_FIELDS,
  display,
  fromDraft,
  toDraft,
  type Draft,
} from './vehicleForm.ts';
import { FieldInputs } from './Vehicle.tsx';

type Unit = { id: string; name: string; kind: string; path: string; parentId: string | null };
type Field = { id: string; key: string; label: string };
type Lookup = RegisterLookup & { exists: string | null };

export function NewVehiclePage() {
  const h1 = usePage(sv.newVehicle.title);
  const navigate = useNavigate();
  const units = useQuery({ queryKey: ['units'], queryFn: () => api<Unit[]>('/vehicles/units') });
  const fields = useQuery({
    queryKey: ['custom-fields'],
    queryFn: () => api<Field[]>('/vehicles/fields'),
  });
  const [plate, setPlate] = useState('');
  const [lookup, setLookup] = useState<Lookup | null>(null);
  const [manual, setManual] = useState(false);
  const [draft, setDraft] = useState<Draft>({});
  const [custom, setCustom] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const fetchPlate = useMutation({
    mutationFn: () => api<Lookup>(`/vehicles/register/${encodeURIComponent(plate.trim())}`),
    onSuccess: (r) => {
      setErrors({});
      setLookup(r);
      setManual(!r.found);
      setDraft({
        ...toDraft({ ...r.fields, registrationNumber: plate.toUpperCase().replace(/\s+/g, '') }),
        orgUnitId: '',
        ownership: 'agd',
        status: r.fields.status ?? 'aktiv',
        vehicleType: r.fields.vehicleType ?? 'personbil',
      });
      setMessage(
        r.found
          ? sv.newVehicle.fetched(sv.sources[r.source] ?? r.source, Object.keys(r.data).length)
          : sv.newVehicle.notFound,
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.errors.length)
        setErrors(Object.fromEntries(err.errors.map((e) => [e.path, e.message])));
      else setMessage(sv.vehicleCard.refreshFailed);
    },
  });
  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/vehicles', {
        method: 'POST',
        body: JSON.stringify({
          ...fromDraft(draft),
          orgUnitId: draft['orgUnitId'] || null,
          customValues: Object.entries(custom).map(([fieldId, value]) => ({ fieldId, value })),
          ...(lookup?.found
            ? {
                register: { source: lookup.source, fetchedAt: lookup.fetchedAt, data: lookup.data },
              }
            : {}),
        }),
      }),
    onSuccess: (r) => navigate(`/fordon/${r.id}?skapad=1`),
    onError: (err) => {
      if (err instanceof ApiError && err.errors.length)
        setErrors(Object.fromEntries(err.errors.map((e) => [e.path, e.message])));
      else setMessage(sv.errors.server);
    },
  });
  const step = lookup || manual ? 2 : 1;
  return (
    <>
      <PageHead
        help="newVehicle"
        crumbs={[{ to: '/fordon', label: sv.vehicleCard.crumb }, { label: sv.newVehicle.crumb }]}
        title={sv.newVehicle.title}
        titleRef={h1}
      />
      <p>{sv.newVehicle.intro}</p>
      <ol className="steps" aria-label={sv.newVehicle.title}>
        <li aria-current={step === 1 ? 'step' : undefined}>{sv.newVehicle.step1}</li>
        <li aria-current={step === 2 ? 'step' : undefined}>{sv.newVehicle.step2}</li>
        <li>{sv.newVehicle.step3}</li>
      </ol>
      <form
        className="form-block"
        onSubmit={(e) => {
          e.preventDefault();
          fetchPlate.mutate();
        }}
      >
        <TextField
          id="registrationNumber"
          label={sv.newVehicle.plate}
          help={sv.newVehicle.plateHelp}
          value={plate}
          required
          error={errors['registrationNumber']}
          onChange={setPlate}
        />
        <div className="form-actions">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={fetchPlate.isPending || plate.trim().length < 2}
            aria-busy={fetchPlate.isPending}
          >
            {fetchPlate.isPending ? sv.newVehicle.fetching : sv.newVehicle.fetch}
          </button>
          {!lookup ? (
            <button
              className="btn"
              type="button"
              onClick={() => {
                setManual(true);
                setDraft({
                  ...toDraft({ registrationNumber: plate.toUpperCase().replace(/\s+/g, '') }),
                  orgUnitId: '',
                  ownership: 'agd',
                  status: 'aktiv',
                  vehicleType: 'personbil',
                });
              }}
            >
              {sv.newVehicle.manual}
            </button>
          ) : null}
        </div>
      </form>
      {message ? (
        <p className="notice" data-state={lookup?.found ? 'done' : 'bad'} role="status">
          {message}
        </p>
      ) : null}
      {lookup?.exists ? (
        <p className="notice" data-state="bad" role="alert">
          {sv.newVehicle.exists}
        </p>
      ) : null}
      {(lookup || manual) && !lookup?.exists ? (
        <form
          className="form-block wide"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <ErrorSummary
            errors={Object.entries(errors)
              .filter(([k]) => k !== 'registrationNumber')
              .map(([k, m]) => ({ id: k, message: m }))}
          />
          {lookup?.found ? (
            <>
              <h2 className="form-heading">{sv.newVehicle.step2}</h2>
              <Facts
                top={false}
                facts={REGISTER_FIELDS.filter((f) => f.key !== 'registrationNumber').map((f) => ({
                  key: f.key,
                  label: sv.vehicleCard.fields[f.key],
                  value: display(f, draft[f.key]) || sv.none,
                  testId: `reg-${f.key}`,
                }))}
              />
            </>
          ) : (
            <>
              <h2 className="form-heading">{sv.newVehicle.step2}</h2>
              <FieldInputs
                defs={REGISTER_FIELDS.filter((f) => f.key !== 'registrationNumber')}
                draft={draft}
                setDraft={setDraft}
                errors={errors}
              />
            </>
          )}
          <h2 className="form-heading">{sv.newVehicle.ownFields}</h2>
          <FieldInputs
            defs={ORG_FIELDS}
            draft={draft}
            setDraft={setDraft}
            errors={errors}
            units={units.data}
          />
          <FieldInputs defs={PLACE_FIELDS} draft={draft} setDraft={setDraft} errors={errors} />
          <h2 className="form-heading">{sv.vehicleCard.sections.economy}</h2>
          <FieldInputs defs={ECONOMY_FIELDS} draft={draft} setDraft={setDraft} errors={errors} />
          <h2 className="form-heading">{sv.vehicleCard.sections.freeText}</h2>
          {fields.data?.map((f) => (
            <TextArea
              key={f.id}
              id={`custom-${f.key}`}
              label={f.label}
              value={custom[f.id] ?? ''}
              rows={2}
              onChange={(v) => setCustom({ ...custom, [f.id]: v })}
            />
          ))}
          <div className="form-actions">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={create.isPending}
              aria-busy={create.isPending}
            >
              {sv.newVehicle.create}
            </button>
          </div>
        </form>
      ) : null}
    </>
  );
}
